import React, { useEffect, useRef, useState } from "react";
import {
  clampGuidePosition,
  normalizePreviewGuides,
} from "./previewGuidesUtils";

const sameGuides = (first, second) =>
  first.enabled === second.enabled &&
  first.vertical.length === second.vertical.length &&
  first.horizontal.length === second.horizontal.length &&
  first.vertical.every((position, index) => position === second.vertical[index]) &&
  first.horizontal.every(
    (position, index) => position === second.horizontal[index],
  );

const PreviewGuides = ({ guides, frameSize, onChange }) => {
  const normalizedGuides = normalizePreviewGuides(guides);
  const [positions, setPositions] = useState(normalizedGuides);
  const positionsRef = useRef(normalizedGuides);
  const overlayRef = useRef(null);
  const dragRef = useRef(null);

  const setLivePositions = (next) => {
    positionsRef.current = next;
    setPositions(next);
  };

  useEffect(() => {
    if (dragRef.current) return;
    const next = normalizePreviewGuides(guides);
    if (!sameGuides(positionsRef.current, next)) {
      setLivePositions(next);
    }
  }, [guides]);

  const updateGuide = (axis, index, position, persist = false) => {
    const next = {
      ...positionsRef.current,
      [axis]: positionsRef.current[axis].map((current, currentIndex) =>
        currentIndex === index ? clampGuidePosition(position) : current,
      ),
    };
    setLivePositions(next);
    if (persist) onChange?.(next);
  };

  const positionFromPointer = (axis, event) => {
    const bounds = overlayRef.current?.getBoundingClientRect();
    if (!bounds) return 0;
    return axis === "vertical"
      ? (event.clientX - bounds.left) / Math.max(1, bounds.width)
      : (event.clientY - bounds.top) / Math.max(1, bounds.height);
  };

  const startDragging = (axis, index, event) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { axis, index, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateGuide(axis, index, positionFromPointer(axis, event));
  };

  const moveGuide = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    updateGuide(
      drag.axis,
      drag.index,
      positionFromPointer(drag.axis, event),
    );
  };

  const finishDragging = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    dragRef.current = null;
    onChange?.(positionsRef.current);
  };

  const handleGuideKeyDown = (axis, index, event) => {
    const negativeKey = axis === "vertical" ? "ArrowLeft" : "ArrowUp";
    const positiveKey = axis === "vertical" ? "ArrowRight" : "ArrowDown";
    if (event.key !== negativeKey && event.key !== positiveKey) return;

    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === positiveKey ? 1 : -1;
    const step = event.shiftKey ? 0.05 : 0.01;
    updateGuide(
      axis,
      index,
      positionsRef.current[axis][index] + direction * step,
      true,
    );
  };

  if (!positions.enabled) return null;

  return (
    <div
      ref={overlayRef}
      className="previewGuidesOverlay"
      data-html2canvas-ignore="true"
      aria-label="Preview guides"
      style={{ width: frameSize.w, height: frameSize.h }}
    >
      {positions.vertical.map((position, index) => (
        <button
          key={`vertical-${index}`}
          type="button"
          role="slider"
          className="previewGuide previewGuide--vertical"
          style={{ left: `${position * 100}%` }}
          aria-label={`Vertical guide ${index + 1}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position * 100)}
          aria-orientation="horizontal"
          onPointerDown={(event) => startDragging("vertical", index, event)}
          onPointerMove={moveGuide}
          onPointerUp={finishDragging}
          onPointerCancel={finishDragging}
          onKeyDown={(event) => handleGuideKeyDown("vertical", index, event)}
        />
      ))}
      {positions.horizontal.map((position, index) => (
        <button
          key={`horizontal-${index}`}
          type="button"
          role="slider"
          className="previewGuide previewGuide--horizontal"
          style={{ top: `${position * 100}%` }}
          aria-label={`Horizontal guide ${index + 1}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position * 100)}
          aria-orientation="vertical"
          onPointerDown={(event) => startDragging("horizontal", index, event)}
          onPointerMove={moveGuide}
          onPointerUp={finishDragging}
          onPointerCancel={finishDragging}
          onKeyDown={(event) => handleGuideKeyDown("horizontal", index, event)}
        />
      ))}
    </div>
  );
};

export default PreviewGuides;
